import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_DOTS = [
  { label: 'Home', href: '#home' },
  { label: 'Approche', href: '#approche' },
  { label: 'Services', href: '#services' },
  { label: 'Cas clients', href: '#cas-clients' },
  { label: 'Contact', href: '#contact' },
];

export default function Hero() {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  /* Track active section for dot nav */
  useEffect(() => {
    const onScroll = () => {
      const sections = NAV_DOTS.map(d => document.querySelector(d.href)).filter(Boolean);
      const scrollY = window.scrollY + window.innerHeight / 3;
      let current = sections[0]?.id || 'home';
      for (const s of sections) {
        if (s.offsetTop <= scrollY) current = s.id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full h-screen overflow-hidden bg-[#0B1829]"
    >
      {/* ── VIDEO BACKGROUND ── */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onCanPlay={() => setVideoReady(true)}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: videoReady ? 1 : 0, transition: 'opacity 1.2s ease' }}
      >
        <source src="/hero.mp4" type="video/mp4" />
      </video>

      {/* ── OVERLAYS ── */}
      {/* Dark cinematic base */}
      <div className="absolute inset-0 bg-[#0B1829]/60" />
      {/* Gradient left → text readability */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(105deg, rgba(11,24,41,0.92) 0%, rgba(11,24,41,0.55) 50%, rgba(11,24,41,0.25) 100%)',
        }}
      />
      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
        style={{ background: 'linear-gradient(to top, #0B1829 0%, transparent 100%)' }}
      />
      {/* Grain */}
      <div className="absolute inset-0 grain-overlay pointer-events-none" style={{ opacity: 0.03 }} />


      {/* ── MAIN HEADLINE — bottom-left, Noah Demeuldre style ── */}
      <div className="absolute bottom-0 left-0 z-20 px-8 md:px-14 pb-28 md:pb-36">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 mb-6"
        >
          <div className="h-px w-8 bg-gradient-to-r from-[#1A6FFF] to-[#00D9A8]" />
          <span className="text-[10px] tracking-[0.35em] uppercase text-white/30 font-medium">
            Agence marketing · Paris
          </span>
        </motion.div>

        {/* Giant headline — condensed bold, staggered reveal */}
        <div className="mb-8">
          {[
            { text: "L'AGENCE QUI FAIT", delay: 0.4 },
            { text: 'PASSER DEVANT', delay: 0.52, accent: true },
            { text: 'LES AMBITIEUX.', delay: 0.64 },
          ].map(({ text, delay, accent }) => (
            <div key={text} className="overflow-hidden leading-none">
              <motion.div
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ duration: 1.0, delay, ease: [0.16, 1, 0.3, 1] }}
              >
                <span
                  className={`block font-black uppercase tracking-[-0.02em] ${accent ? 'gradient-text' : 'text-white'}`}
                  style={{
                    fontSize: 'clamp(2.8rem, 7.5vw, 9rem)',
                    fontFamily: "'DM Sans', sans-serif",
                    lineHeight: 0.92,
                  }}
                >
                  {text}
                </span>
              </motion.div>
            </div>
          ))}
        </div>

        {/* Sub + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row gap-4 sm:items-center"
        >
          <p className="text-white/35 text-sm md:text-base max-w-xs leading-relaxed">
            SEA · SEO · GEO · Web · Automatisation IA.
            <br />Résultats mesurables. Transparents.
          </p>
          <div className="flex gap-3 sm:ml-8">
            <button
              onClick={() => scrollTo('#contact')}
              data-testid="hero-cta-primary"
              className="px-6 py-3 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-[#1A6FFF] to-[#00D9A8] hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300 hover:scale-[1.03] active:scale-95 whitespace-nowrap"
            >
              Démarrer un projet
            </button>
            <button
              onClick={() => scrollTo('#cas-clients')}
              data-testid="hero-cta-secondary"
              className="px-6 py-3 text-sm font-semibold text-white/40 rounded-lg border border-white/10 hover:border-white/25 hover:text-white/75 transition-all duration-300 whitespace-nowrap"
            >
              Voir les résultats
            </button>
          </div>
        </motion.div>
      </div>

      {/* ── DOT NAVIGATION — right side, Noah Demeuldre style ── */}
      <nav
        className="hidden md:flex absolute right-8 md:right-10 top-1/2 -translate-y-1/2 z-30 flex-col items-center gap-5"
        aria-label="Navigation sections"
      >
        {NAV_DOTS.map((dot, i) => (
          <motion.button
            key={dot.href}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 + i * 0.07, duration: 0.5 }}
            onClick={() => scrollTo(dot.href)}
            aria-label={dot.label}
            className="group relative flex items-center justify-end gap-3"
          >
            {/* Label tooltip */}
            <span className="absolute right-6 text-[10px] tracking-widest uppercase text-white/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pr-1">
              {dot.label}
            </span>
            {/* Dot */}
            <span
              className={`block rounded-full transition-all duration-300 ${
                activeSection === dot.href.substring(1)
                  ? 'w-5 h-1.5 bg-gradient-to-r from-[#1A6FFF] to-[#00D9A8]'
                  : 'w-1.5 h-1.5 bg-white/25 group-hover:bg-white/60'
              }`}
            />
          </motion.button>
        ))}
      </nav>

      {/* ── SCROLL INDICATOR ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 pointer-events-none"
      >
        <div className="w-px h-10 overflow-hidden">
          <motion.div
            className="w-px h-full bg-gradient-to-b from-transparent via-white/40 to-transparent"
            animate={{ y: ['-100%', '100%'] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      </motion.div>
    </section>
  );
}
